import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, signal, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { RouterLink, RouterOutlet, RouterLinkActive, Router, ActivatedRoute } from '@angular/router';
import { TuiNavigation } from '@taiga-ui/layout';
import { TuiAppearance, TuiButton } from '@taiga-ui/core';
import { Subscription } from 'rxjs';
import { TuiSkeleton } from '@taiga-ui/kit';
import { NavigationService, NavigationItem } from '../../../shared/services/navigation.service';
import { ApiService } from '../../../shared/services/api.service';
import { RoleService } from '../../../shared/services/role.service';
import { InputHeading } from '../../../shared/components/input-heading/input-heading';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        RouterLinkActive,
        RouterOutlet,
        TuiAppearance,
        TuiButton,
        TuiNavigation,
        InputHeading,
        TuiSkeleton
    ],
    templateUrl: './settings-main.html',
    styleUrl: './settings-main.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class SettingsMain implements OnInit, OnDestroy {
    protected expandedSettings = signal(true);
    protected loadingParking = signal(false);
    protected navigationItems: NavigationItem[] = [];
    protected currentUser: any = null;
    protected userRole: string = 'user';
    private roleSubscription: Subscription = new Subscription();
    protected loader = signal(false);
    private userContextListener = () => { };

    constructor(
        private navigationService: NavigationService,
        private apiService: ApiService,
        private roleService: RoleService,
        protected router: Router,
        private activatedRoute: ActivatedRoute,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.loadSettings();

        // Subscribe to role changes for immediate updates
        this.roleSubscription = this.roleService.currentRole$.subscribe(newRole => {
            this.userRole = newRole;
            this.loadSettings();
        });

        this.userContextListener = () => {
            this.loadSettings();
        };
        window.addEventListener('userContextUpdated', this.userContextListener);
        window.addEventListener('roleChanged', this.userContextListener);
        window.addEventListener('parkingPermissionsLoaded', this.userContextListener);
    }

    private loadSettings(): void {
        this.currentUser = this.apiService.getCurrentUser();
        this.userRole = this.roleService.getCurrentRole();

        let items = this.navigationService.getSettingsNavigationItems();
        if (!items || items.length === 0) {
            items = this.navigationService.getAllSettingsItems();
        }
        this.navigationItems = (items || []).filter(item => this.hasAccess(item));
        this.autoNavigateToFirstSettingsItem();
        this.cdr.markForCheck();
    }

    ngOnDestroy(): void {
        if (this.roleSubscription) {
            this.roleSubscription.unsubscribe();
        }
        window.removeEventListener('userContextUpdated', this.userContextListener);
        window.removeEventListener('roleChanged', this.userContextListener);
        window.removeEventListener('parkingPermissionsLoaded', this.userContextListener);
    }

    /**
     * Check if user has access to a specific navigation item
     */
    hasAccess(item?: NavigationItem | null): boolean {
        if (!item) return false;

        const currentUrl = this.router.url.toLowerCase();
        const segments = currentUrl.split('/').filter(seg => seg.length > 0);
        let povFromUrlDirect: string | null = null;
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (segments.length >= 2 && uuidPattern.test(segments[1])) {
            povFromUrlDirect = segments[0];
        }
        const userPov = (povFromUrlDirect || sessionStorage.getItem('user_pov') || '').toLowerCase();
        const route = (item.route || '').toLowerCase();
        const label = (item.label || '').toLowerCase();

        // Parking context: hide Organization Profile
        if (userPov === 'parking' || currentUrl.includes('/parking/')) {
            if (route.includes('organization-profile') || route.includes('org-profile') || label.includes('organization profile')) {
                return false;
            }
        }
        // Organization context: hide Parking Profile
        else if (userPov === 'organization' || userPov === 'org' || userPov === 'admin' || currentUrl.includes('/organization/') || currentUrl.includes('/org/')) {
            if (route.includes('parking-profile') || label.includes('parking profile')) {
                return false;
            }
        }

        return true;
    }

    /**
     * Check if a settings navigation route is currently active
     */
    protected isRouteActive(route?: string | null): boolean {
        if (!route) return false;
        const currentUrl = this.router.url.split('?')[0].toLowerCase();
        const cleanRoute = route.toLowerCase();

        // Direct includes match
        if (currentUrl.includes(cleanRoute)) {
            return true;
        }

        // Compare last route segments (e.g. 'vehicle-rate' vs 'vehicle-rates')
        const routeSegments = cleanRoute.split('/').filter(Boolean);
        const targetSubRoute = routeSegments.length > 0 ? routeSegments[routeSegments.length - 1] : '';

        const urlSegments = currentUrl.split('/').filter(Boolean);
        const currentSubRoute = urlSegments.length > 0 ? urlSegments[urlSegments.length - 1] : '';

        if (targetSubRoute && currentSubRoute) {
            const normalize = (s: string) => s.replace(/s$/, '');
            if (normalize(targetSubRoute) === normalize(currentSubRoute)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Get role-specific icon for a navigation item
     */
    getRoleSpecificIcon(item?: NavigationItem | null): string {
        if (!item) return '';
        return this.navigationService.getRoleSpecificIcon(item);
    }

    /**
     * Get role-specific label for a navigation item
     */
    getRoleSpecificLabel(item?: NavigationItem | null): string {
        if (!item) return '';
        return this.navigationService.getRoleSpecificLabel(item);
    }

    /**
     * Generate dynamic route URL based on current POV
     */
    getDynamicRoute(route?: string | null): string[] {
        if (!route) return [];
        // Super admin: always use direct routes, no org-scoped URLs
        const currentRole = this.roleService.getCurrentRole();
        if (currentRole === 'super admin') {
            return [route.startsWith('/') ? route : `/${route}`];
        }

        // Parse dynamic context from URL directly (highest priority single source of truth)
        const currentUrl = this.router.url;
        const segments = currentUrl.split('/').filter(seg => seg.length > 0);
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

        let parkingIdFromUrl: string | null = null;
        let povFromUrlDirect: string | null = null;
        if (segments.length >= 2 && uuidPattern.test(segments[1])) {
            parkingIdFromUrl = segments[1];
            povFromUrlDirect = segments[0];
        }

        const userPov = povFromUrlDirect || sessionStorage.getItem('user_pov');
        const parkingId = parkingIdFromUrl || sessionStorage.getItem('parking_id');
        const organizationId = (povFromUrlDirect?.toLowerCase() !== 'parking' ? parkingIdFromUrl : null) || this.apiService.getCurrentOrganizationId() || sessionStorage.getItem('organization_id');

        // Check if route is a settings sub-route
        const settingsSubRoutes = ['org-profile', 'organization-profile', 'parking-profile', 'team', 'passes', 'facilities', 'integrations', 'devices', 'payment-gateway', 'payment-options', 'taxes', 'regions', 'language', 'thermal-printer', 'media-branding', 'vehicle-rate', 'vehicle-rates', 'pass-rates', 'facility-rates', 'facility-setup', 'hardware-integration', 'anpr-module'];

        const routeSegments = route.split('/').filter(Boolean);
        const settingsIndex = routeSegments.indexOf('settings');
        let routeName = route.startsWith('/') ? route.substring(1) : route;

        if (settingsIndex >= 0) {
            routeName = routeSegments.slice(settingsIndex + 1).join('/') || 'settings';
        } else if (routeName.startsWith('settings/')) {
            routeName = routeName.substring('settings/'.length);
        }

        if (userPov && userPov.toLowerCase() === 'parking' && parkingId) {
            // For parking POV
            const urlPrefix = userPov.toLowerCase();
            if (settingsSubRoutes.includes(routeName) || settingsIndex >= 0 || route.startsWith('/settings/')) {
                const subRoute = routeName === 'settings' ? '' : routeName;
                return subRoute ? ['/', urlPrefix, parkingId, 'settings', subRoute] : ['/', urlPrefix, parkingId, 'settings'];
            }
            return ['/', urlPrefix, parkingId, routeName];
        } else if (organizationId) {
            // For organization POV
            const urlPrefix = userPov ? userPov.toLowerCase() : 'organization';
            if (settingsSubRoutes.includes(routeName) || settingsIndex >= 0 || route.startsWith('/settings/')) {
                const subRoute = routeName === 'settings' ? '' : routeName;
                return subRoute ? ['/', urlPrefix, organizationId, 'settings', subRoute] : ['/', urlPrefix, organizationId, 'settings'];
            }
            return ['/', urlPrefix, organizationId, routeName];
        }

        // Fallback to original route
        return [route.startsWith('/') ? route : `/${route}`];
    }



    /**
     * Navigate to a route while preserving current route parameters
     */
    navigateWithPreservedParams(route: string): void {
        // Super admin: always navigate directly, no org-scoped URLs
        const currentRole = this.roleService.getCurrentRole();
        if (currentRole === 'super admin') {
            this.router.navigate([route.startsWith('/') ? route : `/${route}`]);
            return;
        }

        // Get current URL to check if we're in /:pov/:orgId/ format (dynamic pov routes)
        const currentUrl = this.router.url;
        const segments = currentUrl.split('/').filter(seg => seg.length > 0);

        // Extract UUID from URL directly (most reliable source)
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        let parkingIdFromUrl: string | null = null;
        let povFromUrlDirect: string | null = null;
        if (segments.length >= 2 && uuidPattern.test(segments[1])) {
            parkingIdFromUrl = segments[1];
            povFromUrlDirect = segments[0];
        }

        // Check if URL matches /:pov/:orgId/ pattern (where second segment is UUID)
        let isPovScopedRoute = false;
        let orgIdFromUrl: string | null = null;
        let povFromUrl: string | null = null;

        if (segments.length >= 2) {
            const firstSegment = segments[0];
            const secondSegment = segments[1];
            const knownRoutes = ['login', 'signup', 'dashboard', 'settings', 'parkings', 'parking-lots', 'customers', 'reports', 'session', 'passes', 'facilities', 'reservations', 'team', 'billing', 'subscriptions', 'plans', 'transactions', 'organisations', 'admin-dashboard', 'live-dashboard', 'ai', 'sistem-ai-insight', 'vehicle-sessions', 'linked-vehicles', 'data-exports', 'data-export', 'data-import', 'dataimport', 'facility-usage-logs', 'customer', 'reservation', 'parking-add-people', 'parking-add-capacity', 'review-parking', 'subscriptions-plans', 'parking-profile', 'profile', 'upgrade-plan', 'billing-invoice', 'payment-gateway', 'payment-options', 'taxes', 'organization-profile', 'org-profile', 'media-branding', 'passes-details', 'report-view', 'facility-activities', 'setup-guide', 'timezone-settings', 'timezone-demo', 'global-settings', 'role-navigation-demo', 'role-switcher-debug', 'role-switcher-test', 'not-found', 'network-error'];

            // If second segment is a UUID (handles both /parking/:uuid and /organization/:uuid etc.)
            if (uuidPattern.test(secondSegment) && !knownRoutes.includes(firstSegment)) {
                isPovScopedRoute = true;
                orgIdFromUrl = secondSegment;
                povFromUrl = firstSegment;
            } else if (uuidPattern.test(secondSegment)) {
                // Even for known first segments like 'parking', 'organization' etc. extract uuid
                isPovScopedRoute = true;
                orgIdFromUrl = secondSegment;
                povFromUrl = firstSegment;
            }
        }

        // Also check for /org/ format for backward compatibility
        const isOrgScopedRoute = currentUrl.startsWith('/org/') || isPovScopedRoute;

        // Get organization ID from localStorage
        const organizationId = orgIdFromUrl || sessionStorage.getItem('organization_id');

        // Get current role from RoleService
        const isVendor = currentRole === 'vendor';

        // Check if we have vendor context in localStorage
        const vendorContext = sessionStorage.getItem('vendorContext');
        let vendorData = null;

        if (vendorContext) {
            try {
                vendorData = JSON.parse(vendorContext);
            } catch (e) {
                console.error('SettingsMain - Error parsing vendor context:', e);
            }
        }

        // Check if user_pov is "parking" - use parkingId instead of organizationId
        // PRIORITY: Use parking ID from current URL (most reliable) over sessionStorage
        const userPov = sessionStorage.getItem('user_pov');
        // Use URL-extracted parking ID first, then sessionStorage as fallback
        const parkingId = parkingIdFromUrl || sessionStorage.getItem('parking_id') || vendorData?.parkingId;
        // Use pov from URL as the url prefix
        const urlPovFromUrl = povFromUrlDirect || povFromUrl;

        if (parkingIdFromUrl && urlPovFromUrl) {
            // URL has explicit parking context — always prefer this
            const urlPrefix = urlPovFromUrl.toLowerCase();

            // Handle settings routes - extract sub-route name
            let subRoute = '';
            if (route.startsWith('/settings/')) {
                subRoute = route.substring('/settings/'.length);
                console.log('SettingsMain: Extracted subRoute from /settings/ format:', subRoute);
            } else {
                // Check if route is a settings sub-route name
                const settingsSubRoutes = ['org-profile', 'organization-profile', 'parking-profile', 'team', 'passes', 'facilities', 'integrations', 'devices', 'payment-gateway', 'payment-options', 'taxes', 'regions', 'language', 'thermal-printer', 'media-branding', 'vehicle-rate', 'vehicle-rates', 'pass-rates', 'facility-rates', 'facility-setup', 'hardware-integration', 'anpr-module'];
                let routeName = route.startsWith('/') ? route.substring(1) : route;

                if (routeName.startsWith('settings/')) {
                    routeName = routeName.substring('settings/'.length);
                }

                if (settingsSubRoutes.includes(routeName)) {
                    subRoute = routeName;
                    console.log('SettingsMain: Found subRoute in settingsSubRoutes:', subRoute);
                } else if (route.includes('settings')) {
                    // Extract from any settings route format
                    subRoute = route.replace(/^\/?settings\//, '').replace(/^\/?/, '');
                    console.log('SettingsMain: Extracted subRoute from settings route:', subRoute);
                }
            }

            if (subRoute) {
                // Construct absolute URL path
                const absolutePath = `/${urlPrefix}/${parkingId}/settings/${subRoute}`;
                console.log('SettingsMain: Navigating to parking POV route:', absolutePath, 'from original route:', route);

                // Use navigateByUrl for absolute navigation to ensure correct route matching
                this.router.navigateByUrl(absolutePath, { skipLocationChange: false }).then((success) => {
                    if (success) {
                        console.log('SettingsMain: Navigation successful to:', absolutePath);
                    } else {
                        console.error('SettingsMain: Navigation returned false for:', absolutePath);
                    }
                }).catch((error) => {
                    console.error('SettingsMain: Navigation error:', error);
                    console.error('Failed to navigate to:', absolutePath);
                    // Fallback: try with navigate array format
                    console.log('SettingsMain: Trying fallback navigation with array format');
                    this.router.navigate([urlPrefix, parkingId, 'settings', subRoute]).catch((fallbackError) => {
                        console.error('SettingsMain: Fallback navigation also failed:', fallbackError);
                    });
                });
                return;
            } else {
                console.warn('️ SettingsMain: No subRoute found for route:', route);
                console.warn('️ SettingsMain: userPov:', userPov, 'parkingId:', parkingId);
            }
        }

        // If we have organization ID, check if we're in /:pov/ format and preserve it
        if (organizationId) {
            // Get user_pov value for URL prefix
            const urlPrefix = userPov ? userPov.toLowerCase() : 'organization';

            // If current route is in /:pov/:orgId/ format (any page), use /:pov/:orgId/settings/:subRoute format
            if (isPovScopedRoute && orgIdFromUrl) {
                // Use the pov from URL if available, otherwise use user_pov from localStorage
                const finalUrlPrefix = povFromUrl || urlPrefix;
                // Handle routes that start with /settings/ (e.g., /settings/org-profile)
                let cleanRoute = route.startsWith('/') ? route.substring(1) : route;

                // If route starts with 'settings/', remove it to get just the sub-route
                if (cleanRoute.startsWith('settings/')) {
                    cleanRoute = cleanRoute.substring('settings/'.length);
                }

                // Navigate to /:pov/:orgId/settings/:subRoute
                this.router.navigate([finalUrlPrefix, organizationId, 'settings', cleanRoute]);
                return;
            }
            // Also check if route itself is a settings route and we have orgId
            // This handles cases where user is on /:pov/:orgId/dashboard and clicks settings item
            if (route.startsWith('/settings/')) {
                const subRoute = route.substring('/settings/'.length);
                this.router.navigate([urlPrefix, organizationId, 'settings', subRoute]);
                return;
            }
            // Handle routes that are just sub-route names (e.g., 'org-profile' or '/org-profile')
            // Check if it's a known settings sub-route
            const settingsSubRoutes = ['org-profile', 'organization-profile', 'parking-profile', 'team', 'passes', 'facilities', 'integrations', 'devices', 'payment-gateway', 'taxes', 'regions', 'language', 'thermal-printer', 'media-branding', 'vehicle-rate', 'vehicle-rates', 'pass-rates', 'facility-rates', 'facility-setup', 'hardware-integration', 'anpr-module', 'payment-options'];
            let routeName = route.startsWith('/') ? route.substring(1) : route;

            // If route starts with 'settings/', extract the sub-route name
            if (routeName.startsWith('settings/')) {
                routeName = routeName.substring('settings/'.length);
            }

            if (settingsSubRoutes.includes(routeName)) {
                // Always use pov-scoped format when we have organizationId
                this.router.navigate([urlPrefix, organizationId, 'settings', routeName]);
                return;
            }

            // If route is a settings route but not in the list, still use pov-scoped format
            if (route.includes('settings') || route.startsWith('/settings/')) {
                const subRoute = route.startsWith('/settings/') ? route.substring('/settings/'.length) : route.replace('settings/', '');
                this.router.navigate([urlPrefix, organizationId, 'settings', subRoute]);
                return;
            }

            // Otherwise use old format for backward compatibility (non-settings routes)
            // BUT: Skip this if the route is already an absolute POV-scoped path (like /organization/ID/dashboard)
            const isAbsolutePovRoute = route.startsWith('/') && (route.includes('/organization/') || route.includes('/parking/') || route.split('/').length > 2);

            if (!isAbsolutePovRoute) {
                this.router.navigate([route, organizationId]);
            } else {
                this.router.navigateByUrl(route);
            }
            return;
        }

        // Fallback to basic navigation
        this.router.navigate([route]);
    }

    /**
     * Extract route parameters from current URL
     */
    private extractRouteParamsFromUrl(url: string): { parkingId?: string, parkingName?: string, organizationId?: string } {
        // Remove query parameters from URL
        const pathOnly = url.split('?')[0];
        const segments = pathOnly.split('/').filter(segment => segment.length > 0);

        // Check if URL format is /route/orgId/parkingId/parkingName (vendor format)
        if (segments.length >= 4) {
            const orgId = segments[segments.length - 3];
            const parkingId = segments[segments.length - 2];
            const parkingName = segments[segments.length - 1];

            // Validate parkingId (should be UUID format)
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
            if (uuidRegex.test(parkingId)) {
                return { organizationId: orgId, parkingId, parkingName };
            }
        }

        // Check if URL format is /route/parkingId/parkingName (old format)
        if (segments.length >= 3) {
            const parkingId = segments[segments.length - 2];
            const parkingName = segments[segments.length - 1];

            // Validate parkingId (should be UUID format)
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
            if (uuidRegex.test(parkingId)) {
                return { parkingId, parkingName };
            }
        }

        return {};
    }

    /**
     * Parse query parameters from URL string
     */
    private parseQueryParams(queryString: string): any {
        const params: any = {};
        const pairs = queryString.split('&');

        for (const pair of pairs) {
            const [key, value] = pair.split('=');
            if (key && value) {
                params[decodeURIComponent(key)] = decodeURIComponent(value);
            }
        }

        return params;
    }

    // Format parking name for URL
    private formatParkingNameForUrl(parkingName: string): string {
        if (!parkingName) return '';
        return parkingName
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
            .trim();
    }

    /**
     * Auto-navigate to first settings item if we're on the base settings route
     */
    private autoNavigateToFirstSettingsItem(): void {
        const currentUrl = this.router.url;
        const urlSegments = currentUrl.split('/').filter(segment => segment.length > 0);
        const lastSegment = urlSegments[urlSegments.length - 1];

        // Match any URL whose last segment is 'settings':
        //   /settings
        //   /settings/{uuid}
        //   /parking/{uuid}/settings
        //   /organization/{uuid}/settings
        const isBaseSettingsRoute = lastSegment === 'settings';

        if (isBaseSettingsRoute) {
            const currentRole = this.roleService.getCurrentRole();
            // Super admin: navigate directly to first settings item route (no org-scoped URL)
            if (currentRole === 'super admin') {
                setTimeout(() => {
                    if (this.navigationItems && this.navigationItems.length > 0) {
                        const firstItem = this.navigationItems[0];
                        this.router.navigate([firstItem.route]);
                    }
                }, 50);
                return;
            }

            setTimeout(() => {
                const latestUrl = this.router.url.split('?')[0];
                const latestSegments = latestUrl.split('/').filter(segment => segment.length > 0);
                const latestLastSegment = latestSegments[latestSegments.length - 1];
                if (latestLastSegment === 'settings' && this.navigationItems && this.navigationItems.length > 0) {
                    const firstSettingsItem = this.navigationItems[0];
                    this.navigateWithPreservedParams(firstSettingsItem.route);
                }
            }, 50);
        }
    }
}
